import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DrawerSetting } from '@shared/models/drawer/drawer-setting';
import {
  GridColumn,
  GridTemplateCell,
} from '@shared/modules/grid/abstractions';
import { DrawerManagerService } from '@shared/services/drawer/drawer-manager.service';
import {
  ExternalUser,
  InternalUser,
  UserEngagementAssignment,
  UserEngagementDrawerData,
} from '../../models';
import { UserEngagementsDrawerComponent } from '../user-engagements-drawer/user-engagements-drawer.component';

type UserWithEngagements = InternalUser | ExternalUser;

@Component({
  standalone: false,
  selector: 'gstar-user-engagements-cell',
  templateUrl: './user-engagements-cell.component.html',
  styleUrl: './user-engagements-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserEngagementsCellComponent
  implements GridTemplateCell<UserWithEngagements>
{
  private readonly drawerManagerService = inject(DrawerManagerService);

  header = signal<GridColumn | undefined>(undefined);
  row = signal<UserWithEngagements | undefined>(undefined);

  readonly assignments = computed<UserEngagementAssignment[]>(
    () => this.row()?.engagementAssignments ?? []
  );

  readonly summaryAssignments = computed(() => this.assignments().slice(0, 2));

  readonly hiddenAssignmentsCount = computed(() =>
    Math.max(this.assignments().length - this.summaryAssignments().length, 0)
  );

  readonly summaryText = computed(() =>
    this.summaryAssignments()
      .map(assignment =>
        assignment.isActive
          ? assignment.clientName
          : `${assignment.clientName} (Inactive)`
      )
      .join(', ')
  );

  /**
   * Creates and opens the user engagements drawer.
   * @returns void function
   */
  openDrawer(): void {
    if (!this.assignments().length || !this.row()) {
      return;
    }

    const drawerSetting = new DrawerSetting<UserEngagementDrawerData, void>();
    drawerSetting.title = 'admin.user.engagementsDrawerTitle';
    drawerSetting.component = UserEngagementsDrawerComponent;
    drawerSetting.hideFooterButtons = true;
    drawerSetting.mask = true;
    drawerSetting.data = {
      userFullName: this.row()!.fullName,
      email: this.row()!.email,
      assignments: this.assignments(),
    };

    this.drawerManagerService.open(drawerSetting);
  }
}
